import { useState } from 'react';
import { Play } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { PrixiaPlayer } from '@/remotion/PrixiaPlayer';

export function VideoSection() {
  const [playing, setPlaying] = useState(false);

  return (
    <section className="relative py-16 sm:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-semibold mb-4">
              <Play className="w-3.5 h-3.5" />
              Video de Presentación
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Mirá Prixia en acción
            </h2>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
              En menos de 2 minutos descubrí cómo Prixia transforma tu proceso de cotización,
              monitorea competidores y te da el control total de tu pricing.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.15}>
          <div className="relative rounded-2xl overflow-hidden border border-gray-200/60 dark:border-gray-700/50 shadow-2xl">
            {/* Cover / Poster */}
            <AnimatePresence>
              {!playing && (
                <motion.div
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0 z-10 cursor-pointer"
                  onClick={() => setPlaying(true)}
                >
                  {/* Gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-700 via-indigo-600 to-violet-600" />

                  {/* Decorative elements */}
                  <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5" />
                  <div className="absolute bottom-10 -left-10 w-40 h-40 rounded-full bg-white/5" />
                  <div className="absolute top-1/4 right-1/3 w-24 h-24 rounded-full bg-white/10" />

                  {/* Dot grid */}
                  <div
                    className="absolute inset-0 opacity-10"
                    style={{
                      backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
                      backgroundSize: '24px 24px',
                    }}
                  />

                  {/* Content */}
                  <div className="relative z-10 flex flex-col items-center justify-center h-full gap-6">
                    {/* Logo */}
                    <img
                      src="/prixia-logo-white.svg"
                      alt="Prixia"
                      className="h-12 sm:h-16 w-auto"
                    />

                    {/* Play button */}
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-[0_0_60px_rgba(255,255,255,0.2)]"
                    >
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white flex items-center justify-center">
                        <Play
                          className="w-7 h-7 sm:w-8 sm:h-8 text-indigo-700 ml-1"
                          fill="currentColor"
                        />
                      </div>
                    </motion.div>

                    {/* Text */}
                    <div className="text-center">
                      <p className="text-white font-bold text-lg sm:text-xl mb-1">
                        Transforma tu estrategia de precios
                      </p>
                      <p className="text-indigo-200 text-sm">1:27 min — Presentación comercial</p>
                    </div>

                    {/* Feature pills */}
                    <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                      {['Cotizaciones IA', 'Pricing Dinámico', 'Analytics', 'Importaciones'].map(
                        (t) => (
                          <span
                            key={t}
                            className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-medium"
                          >
                            {t}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Player (always mounted, starts playing when cover is dismissed) */}
            <div style={{ aspectRatio: '16/9' }}>
              <PrixiaPlayer autoPlay={playing} controls loop={false} />
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
